/**
 * Note write telemetry.
 *
 * Records each Notes screen write (save, update, delete, attachment) that
 * fails. Before this existed a failed write left the note dialog stuck on
 * "Saving..." and the failure never reached the server, so it was invisible.
 * Most failures are a lost connection, so records are kept in localStorage
 * and sent through jserror/log after the next successful Notes request.
 * Records never include note content.
 */

// NOTE: api is accessed lazily via (window as any), matching session-check-telemetry.ts.

import type { AjaxFailureInfo } from '@/services/api-class';

export type NoteWriteAction = 'save' | 'update' | 'delete' | 'attachment';

interface NoteWriteFailureRecord {
  event: 'note-write-failed';
  action: NoteWriteAction;
  /** HTTP status, 0 when no response arrived */
  status: number;
  timedOut: boolean;
  durationMs: number;
  at: string;
  online: boolean;
  connection: string;
  visibility: string;
  platform: string;
}

const STORAGE_KEY = 'NoteWriteTelemetry';
const MAX_RECORDS = 20;

function loadRecords(): NoteWriteFailureRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveRecords(records: NoteWriteFailureRecord[]): void {
  try {
    if (records.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(-MAX_RECORDS)));
    }
  } catch (e) {
    // Storage full or unavailable — telemetry is best-effort
  }
}

function getPlatform(): string {
  const w = window as any;
  if (w.isIOSApp) return 'ios-app';
  if (w.isAndroidApp) return 'android-app';
  if (w.isWindowsAppSdkApp) return 'windows-app';
  return 'browser';
}

function getConnection(): string {
  const c = (navigator as any).connection;
  if (!c) return 'n/a';
  return [c.type, c.effectiveType].filter(Boolean).join('/') || 'n/a';
}

export function recordNoteWriteFailure(
  action: NoteWriteAction,
  result: any,
  failure: AjaxFailureInfo | undefined,
  startedAt: number
): void {
  const status = result && typeof result.status === 'number' ? result.status : 0;
  const records = loadRecords();
  records.push({
    event: 'note-write-failed',
    action,
    status,
    timedOut: failure?.timedOut === true,
    durationMs: Math.round(Date.now() - startedAt),
    at: new Date().toISOString(),
    online: navigator.onLine,
    connection: getConnection(),
    visibility: document.visibilityState,
    platform: getPlatform(),
  });
  saveRecords(records);

  // The server answered, so the connection works and the record can go now.
  if (status > 0) {
    flushNoteWriteTelemetry();
  }
}

/** Send queued records; call after a Notes request succeeds. */
export function flushNoteWriteTelemetry(): void {
  const records = loadRecords();
  if (records.length === 0) return;

  const w = window as any;
  if (!w.api || !w.JSErrorType) return;

  saveRecords([]);
  for (const r of records) {
    w.api.insertNewJSError('NoteWrite ' + JSON.stringify(r), null, r.event, w.JSErrorType.CAUGHT, null);
  }
}
